/**
 * O Brasil, para a cotação de deslocamento só calcular dentro dele (operador,
 * 04/10). A chave do Google não se restringe por país, então o limite mora
 * aqui: o servidor recusa ponto fora antes de pedir rota (e de pagar por ela),
 * e o mapa devolve o marcador solto fora do Brasil.
 *
 * ── fonte ────────────────────────────────────────────────────────────────────
 * Natural Earth, Admin 0 – Countries, escala 1:10m, v5.1.2 (domínio público):
 * `geojson/ne_10m_admin_0_countries.geojson` de github.com/nvkelso/natural-earth-vector
 * (commit 9380cca, 13/05/2022), feição `ADM0_A3 = BRA` — 43 polígonos, 11.121
 * vértices. O 1:50m foi descartado: não tem Fernando de Noronha nem Trindade.
 *
 * ── simplificação PARA FORA ──────────────────────────────────────────────────
 * Um Douglas-Peucker comum corta o Brasil para dentro, e com algumas centenas
 * de vértices o corte passa de 10 km nas fronteiras de rio — recusaria Foz,
 * Chuí e Tabatinga. Aqui o polígono é alargado ANTES de simplificar, e só erra
 * para fora:
 *
 *   1. folga de 5 km em volta de tudo; 10 km nas ilhas oceânicas, que o Natural
 *      Earth desenha 3–8 km fora da posição real (Trindade, São Pedro e São
 *      Paulo);
 *   2. Douglas-Peucker com tolerância de 2,5 km, preservando topologia, sem
 *      buracos (as distâncias numa azimutal equidistante centrada em −14,2,
 *      −51,9);
 *   3. coordenadas com 3 casas (~110 m).
 *
 * Resultado: 5 anéis, 946 vértices (continente com as ilhas costeiras, que a
 * folga une a ele; Fernando de Noronha; Trindade; Martim Vaz; São Pedro e São
 * Paulo). Atol das Rocas e Abrolhos NÃO estão na fonte e ficam de fora.
 *
 * ── o que foi medido (contra a fonte inteira, não contra a simplificação) ───
 * - os 118.486 pontos da fronteira da fonte (vértices + arestas a cada 250 m)
 *   ficam TODOS dentro, o mais apertado a 2,45 km da borda: o "Brasil + ~5 km"
 *   nunca é menos que Brasil + 2,45 km;
 * - a borda vai no máximo 7,4 km além da fronteira em terra (10 km em volta
 *   das ilhas oceânicas);
 * - a própria fonte erra até ~3,5 km nas fronteiras de rio (põe Cobija,
 *   Leticia e Puerto Quijarro dentro do Brasil): a folga é o que absorve isso.
 *
 * Consequência aceita: cidade-gêmea colada na fronteira passa (Ciudad del
 * Este, Puerto Quijarro, Rivera, Chuy, Leticia, Pedro Juan Caballero, Paso de
 * los Libres…). O que importa é o contrário — nenhum lugar do Brasil é
 * recusado. Os casos estão em brazil-boundary.test.ts (apps/api).
 */
import { BRAZIL_RINGS } from './brazil-boundary.data';

export { BRAZIL_RINGS };

interface Box {
  south: number;
  west: number;
  north: number;
  east: number;
}

function boxOf(ring: ReadonlyArray<number>): Box {
  const box = { south: Infinity, west: Infinity, north: -Infinity, east: -Infinity };
  for (let i = 0; i < ring.length; i += 2) {
    const lng = ring[i]!;
    const lat = ring[i + 1]!;
    if (lat < box.south) box.south = lat;
    if (lat > box.north) box.north = lat;
    if (lng < box.west) box.west = lng;
    if (lng > box.east) box.east = lng;
  }
  return box;
}

const RING_BOXES: Box[] = BRAZIL_RINGS.map(boxOf);

/** o retângulo que contém o polígono inteiro (com as ilhas oceânicas) — o mapa parte dele */
export const BRAZIL_BOUNDS: Readonly<Box> = RING_BOXES.reduce((acc, b) => ({
  south: Math.min(acc.south, b.south),
  west: Math.min(acc.west, b.west),
  north: Math.max(acc.north, b.north),
  east: Math.max(acc.east, b.east),
}));

const inBox = (b: Box, lat: number, lng: number) =>
  lat >= b.south && lat <= b.north && lng >= b.west && lng <= b.east;

/** par-ímpar (raio para leste) num anel [lng, lat, …] aberto */
function inRing(ring: ReadonlyArray<number>, lat: number, lng: number): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 2; i < n; j = i, i += 2) {
    const xi = ring[i]!;
    const yi = ring[i + 1]!;
    const xj = ring[j]!;
    const yj = ring[j + 1]!;
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * A posição fica no Brasil (com a folga de ~5 km para fora descrita acima)?
 * Pura e síncrona: roda na API, antes de qualquer consulta ao provedor, e no
 * navegador, no fim do arraste de um marcador. Cada anel é testado por si —
 * par-ímpar somado entre anéis daria "fora" num ponto coberto por dois.
 */
export function isInBrazil(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (!inBox(BRAZIL_BOUNDS, lat, lng)) return false;
  for (let k = 0; k < BRAZIL_RINGS.length; k++) {
    if (inBox(RING_BOXES[k]!, lat, lng) && inRing(BRAZIL_RINGS[k]!, lat, lng)) return true;
  }
  return false;
}
